import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';

import { ActionBar, ActionButton } from '../common/action-bar';
import { Dropdown } from '../common/dropdown';
import { ClearableSearchBar } from '../common/searchBoxWithClear';
import Table from '../common/table';
import { MessageLocation } from '../../common/utils/stringConstants';
import { filterItemsV2 } from '../../common/utils/generalFunctions';
import DraftOrdersRow from './draft-orders-row';
import * as selectors from '../../selectors';
import * as draftOrdersActions from '../../actions/mpOnlineActions';

const sortByDeliveryDate = (orders) =>
  [...orders].sort((a, b) => {
    const dateA = new Date(a.deliveryDate || a.deliveryDateDisplay || 0);
    const dateB = new Date(b.deliveryDate || b.deliveryDateDisplay || 0);
    return dateA - dateB;
  });

const getOrderGuideIdFromDraftOrder = (draftOrder) => {
  const onlineInfo = draftOrder && (draftOrder.purchaseOrderOnlineOrderInfo || draftOrder.PurchaseOrderOnlineOrderInfo || {});

  return draftOrder.orderGuideHeaderId
    || draftOrder.OrderGuideHeaderId
    || draftOrder.selectedOrderGuideId
    || draftOrder.SelectedOrderGuideId
    || onlineInfo.selectedOrderGuideId
    || onlineInfo.SelectedOrderGuideId
    || 0;
};

const getOrderGuideNameFromDraftOrder = (draftOrder) => {
  const onlineInfo = draftOrder && (draftOrder.purchaseOrderOnlineOrderInfo || draftOrder.PurchaseOrderOnlineOrderInfo || {});

  return draftOrder.guideName
    || draftOrder.GuideName
    || draftOrder.selectedGuideName
    || draftOrder.SelectedGuideName
    || onlineInfo.selectedGuideName
    || onlineInfo.SelectedGuideName
    || '';
};

const buildDraftOrderCounts = (draftOrders) => {
  const countsByGuideId = {};
  const countsByGuideName = {};

  (draftOrders || []).forEach((draftOrder) => {
    const guideId = getOrderGuideIdFromDraftOrder(draftOrder);
    const guideName = getOrderGuideNameFromDraftOrder(draftOrder);

    if (guideId) {
      countsByGuideId[guideId] = (countsByGuideId[guideId] || 0) + 1;
      return;
    }

    if (guideName) {
      countsByGuideName[guideName] = (countsByGuideName[guideName] || 0) + 1;
    }
  });

  return { countsByGuideId, countsByGuideName };
};

const normalizeOrderGuideOptions = (orderGuides, draftOrders) => {
  const { countsByGuideId, countsByGuideName } = buildDraftOrderCounts(draftOrders);

  return (orderGuides || []).map((guide, index) => {
    const key = typeof guide.key !== 'undefined' ? guide.key : (guide.orderGuideHeaderId || index);
    const baseText = guide.guideName || guide.text || `Order Guide ${index + 1}`;
    const count = guide.orderGuideHeaderId
      ? (countsByGuideId[guide.orderGuideHeaderId] || 0)
      : (countsByGuideName[guide.guideName || ''] || 0);

    return {
      ...guide,
      key,
      text: `${baseText} (${count})`
    };
  });
};

const headers = [
  { header: 'Web Order Number', className: 'setWidth200', name: 'poHeaderId' },
  { header: 'Delivery Date', className: 'table-cell-center setWidth100', name: 'deliveryDateDisplay' },
  { header: 'Order Cutoff Date/Time', className: 'setWidth200', name: 'orderCutoffDateTime' },
  { header: 'Total', className: 'table-cell-center setWidth100 isCurrency', name: 'total' },
  { header: 'Lines', className: 'table-cell-center setWidth80', name: 'lineCount' },
  { header: 'Create Date', className: 'setWidth100', name: 'createDate' },
  { header: 'Created By', className: 'setWidth100', name: 'createdBy' },
  { header: 'Last Modified Date', className: 'setWidth100', name: 'lastModifiedDate' },
  { header: 'Last Modified By', className: 'setWidth100', name: 'lastModifiedBy' },
  { header: 'PO Number', className: 'setWidth200', name: 'purchaseOrderNumber' },
  { header: 'Vendor Number', className: 'setWidth200', name: 'storeNumber' },
  { header: 'Notes', className: 'width300', name: 'orderNote' }
];

export class DraftOrders extends React.Component {
  static propTypes = {
    account: PropTypes.object,
    draftOrders: PropTypes.array,
    orderGuides: PropTypes.array,
    selectedOrderGuide: PropTypes.object,
    draftOrdersActions: PropTypes.object,
    history: PropTypes.object
  };

  constructor(props) {
    super(props);

    const sourceOrders = props.draftOrders || [];
    const orderGuides = normalizeOrderGuideOptions(props.orderGuides || [], sourceOrders);
    const selectedOption = this.getSelectedOrderGuide(orderGuides, props.selectedOrderGuide);
    this.state = {
      filterText: '',
      orderGuides,
      selectedOption,
      filteredOrders: this.getFilteredOrders(sourceOrders, '', selectedOption)
    };

    this.ClearTextField = this.ClearTextField.bind(this);
  }

  componentDidMount() {
    this.searchDraftOrders();
    this.loadOrderGuides();

    const { selectedOption } = this.state;
    if (selectedOption && selectedOption.orderGuideHeaderId) {
      this.selectOrderGuide(selectedOption);
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const newState = {};

    if (nextProps.orderGuides !== this.props.orderGuides) {
      const nextOrderGuides = normalizeOrderGuideOptions(nextProps.orderGuides || [], nextProps.draftOrders || []);
      const selectedOption = this.getSelectedOrderGuide(nextOrderGuides, nextProps.selectedOrderGuide);

      newState.orderGuides = nextOrderGuides;
      newState.selectedOption = selectedOption;
      newState.filteredOrders = this.getFilteredOrders(nextProps.draftOrders || [], this.state.filterText, selectedOption);

      if (selectedOption && selectedOption.orderGuideHeaderId) {
        this.selectOrderGuide(selectedOption, nextProps.account);
      }
    }

    if (this.props.draftOrders !== nextProps.draftOrders) {
      const nextOrderGuides = normalizeOrderGuideOptions(nextProps.orderGuides || [], nextProps.draftOrders || []);
      const selectedOptionSeed = typeof newState.selectedOption !== 'undefined' ? newState.selectedOption : this.state.selectedOption;
      const selectedOption = this.getSelectedOrderGuide(nextOrderGuides, selectedOptionSeed);

      newState.orderGuides = nextOrderGuides;
      newState.selectedOption = selectedOption;
      newState.filteredOrders = this.getFilteredOrders(nextProps.draftOrders || [], this.state.filterText, selectedOption);
    }

    if (Object.keys(newState).length > 0) {
      this.setState(newState);
    }
  }

  getDefaultOrderGuide = (orderGuides) => {
    if (!orderGuides || orderGuides.length === 0) {
      return {};
    }

    const defaultIndex = orderGuides.findIndex(x => x && x.isDefault);
    return defaultIndex >= 0 ? orderGuides[defaultIndex] : orderGuides[0];
  };

  getSelectedOrderGuide = (orderGuides, selectedOrderGuide) => {
    if (!orderGuides || orderGuides.length === 0) {
      return {};
    }

    if (selectedOrderGuide && selectedOrderGuide.orderGuideHeaderId) {
      const existing = orderGuides.find(x => x.orderGuideHeaderId === selectedOrderGuide.orderGuideHeaderId);
      if (existing) {
        return existing;
      }
    }

    return this.getDefaultOrderGuide(orderGuides);
  };

  getPersistedOrderGuideOption = (option) => {
    if (!option) {
      return option;
    }

    const sourceOrderGuides = this.props.orderGuides || [];
    const matchingGuide = sourceOrderGuides.find(guide =>
      guide && option && guide.orderGuideHeaderId === option.orderGuideHeaderId
    );

    if (matchingGuide) {
      return {
        ...matchingGuide,
        key: typeof matchingGuide.key !== 'undefined'
          ? matchingGuide.key
          : (matchingGuide.orderGuideHeaderId || option.key),
        text: matchingGuide.text || matchingGuide.guideName || option.guideName || option.text
      };
    }

    const cleanText = option.text ? option.text.replace(/\s\(\d+\)$/, '') : option.text;

    return {
      ...option,
      text: cleanText
    };
  };

  getOrderGuideIdFromDraftOrder = (draftOrder) => {
    return getOrderGuideIdFromDraftOrder(draftOrder);
  };

  getOrderGuideNameFromDraftOrder = (draftOrder) => {
    return getOrderGuideNameFromDraftOrder(draftOrder);
  };

  getFilteredOrders = (orders, filterText, selectedOption) => {
    const sourceOrders = orders || [];
    const selectedGuideId = selectedOption && selectedOption.orderGuideHeaderId ? selectedOption.orderGuideHeaderId : 0;
    const selectedGuideName = selectedOption && selectedOption.guideName ? selectedOption.guideName : '';

    const guideFilteredOrders = selectedGuideId || selectedGuideName
      ? sourceOrders.filter(order => {
          const orderGuideId = this.getOrderGuideIdFromDraftOrder(order);
          const orderGuideName = this.getOrderGuideNameFromDraftOrder(order);

          if (selectedGuideId && orderGuideId) {
            return orderGuideId === selectedGuideId;
          }

          if (selectedGuideName) {
            return orderGuideName === selectedGuideName;
          }

          return true;
        })
      : sourceOrders;

    if (!filterText || filterText.trim() === '') {
      return sortByDeliveryDate(guideFilteredOrders);
    }

    const matchedItems = filterItemsV2(guideFilteredOrders, filterText.toLowerCase().trim());
    return sortByDeliveryDate(matchedItems);
  };

  parseBooleanFilterValue = (value) => {
    if (value === '') {
      return null;
    }

    return value === 'true';
  };

  onBooleanFilterChange = (event, stateKey) => {
    this.setState({ [stateKey]: this.parseBooleanFilterValue(event.target.value) });
  };

  filter = (event) => {
    const draftOrders = this.props.draftOrders || [];
    const { selectedOption } = this.state;
    const inputValue = event.target.value.toLowerCase().trim();

    this.setState({
      filterText: event.target.value,
      filteredOrders: this.getFilteredOrders(draftOrders, inputValue, selectedOption)
    });
  };

  loadOrderGuides = () => {
    const { account, draftOrdersActions } = this.props;

    if (account && account.accountId) {
      draftOrdersActions.orderGuide(account.accountId);
    }
  };

  selectOrderGuide = (option, accountOverride) => {
    const account = accountOverride || this.props.account;
    const conceptId = account && typeof account.conceptId !== 'undefined' ? account.conceptId : undefined;
    const persistedOption = this.getPersistedOrderGuideOption(option);

    this.props.draftOrdersActions.setSelectedOrderGuide(persistedOption);
    if (persistedOption && persistedOption.vendorId) {
      this.props.draftOrdersActions.getVendor(persistedOption.vendorId, conceptId);
    }
  };

  onOrderGuideChange = (option) => {
    const draftOrders = this.props.draftOrders || [];

    this.setState({
      selectedOption: option,
      filteredOrders: this.getFilteredOrders(draftOrders, this.state.filterText, option)
    });

    this.selectOrderGuide(option);
  };

  searchDraftOrders = () => {
    const { account, draftOrdersActions } = this.props;

    if (account && account.accountId) {
      draftOrdersActions.searchDraftOrders(account.accountId, true, false, false, false);
    }
  };

  retrieveExcel = () => {
    // Placeholder for Draft Orders Excel export wiring.
  };

  retrievePdf = () => {
    // Placeholder for Draft Orders PDF export wiring.
  };

  retrieveDetail = (draftOrder) => {
    const headerId = draftOrder && (draftOrder.headerId || draftOrder.poHeaderId);

    this.props.history.push({
      pathname: '/Edit-Draft-Order',
      state: { headerId }
    });
  };

  ClearTextField = (e) => {
    if (e.currentTarget.onclick) {
      const draftOrders = this.props.draftOrders || [];
      this.setState({
        filterText: '',
        filteredOrders: this.getFilteredOrders(draftOrders, '', this.state.selectedOption)
      });
    }
  };

  render() {
    const { account } = this.props;
    const color = account && account.conceptColor ? account.conceptColor : '#4E99CD';
    const { filteredOrders, filterText, orderGuides, selectedOption } = this.state;
    const totalDraftOrders = (this.props.draftOrders || []).length;
    const cssClasses = 'fa fa-2x vertical-align-middle';

    if (!account || !account.accountId) {
      return (<Redirect to="/" />);
    }

    return (
      <div className="container-fluid">
        <ActionBar conceptColor={color}>
          <ClearableSearchBar
            filterText={filterText}
            multiFilter={this.filter}
            ClearTextField={this.ClearTextField} />

          <Dropdown dropdownId="ddOrderGuide"
            options={orderGuides}
            selectedOption={selectedOption}
            onSelectionChange={this.onOrderGuideChange} />

          <span id="txtTotalDraftOrders" className="draft-orders-total-count">
            Total Draft Orders (All Order Guides): {totalDraftOrders}
          </span>

          <ActionButton id="btnDraftSearch"
            text="Load Draft Orders"
            className={`fa-search ${cssClasses}`}
            clickHandler={this.searchDraftOrders} />
        </ActionBar>

        <Table id="tblDraftOrders"
          headers={headers}
          items={filteredOrders}
          pageSize={10}
          RowComponent={DraftOrdersRow}
          callback={this.retrieveDetail}
          wrapperClassName="tableWrapper"
          tableClassName="fixed_header"
          filterTypes={[MessageLocation.HeaderBanner]} />
      </div>
    );
  }
}

export const mapStateToProps = state => {
  return {
    account: selectors.getAccount(state),
    draftOrders: state && state.draftOrders ? state.draftOrders : [],
    orderGuides: selectors.orderGuides(state),
    selectedOrderGuide: selectors.getSelectedOrderGuide(state)
  };
};

export const mapDispatchToProps = dispatch => {
  return {
    draftOrdersActions: {
      orderGuide: (accountId) => dispatch(draftOrdersActions.orderGuide(accountId)),
      setSelectedOrderGuide: (selectedOrderGuide) => dispatch(draftOrdersActions.setSelectedOrderGuide(selectedOrderGuide)),
      getVendor: (vendorId, conceptId) => dispatch(draftOrdersActions.getVendor(vendorId, conceptId)),
      searchDraftOrders: (accountId, createdAsDraft, isApproved, isCancelled, isSubmitted, isQueued) =>
        dispatch(draftOrdersActions.searchDraftOrders(accountId, createdAsDraft, isApproved, isCancelled, isSubmitted, isQueued))
    }
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(DraftOrders);
